<template>
  <div class="physical-performance-trends-enhanced">
    <!-- Controls Row -->
    <div class="controls-section mb-4">
      <div class="row">
        <!-- Chart View Type -->
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Chart View</label>
          <b-button-group class="w-100 mobile-button-group">
            <b-button :pressed="chartViewType === 'line'" @click="setChartViewType('line')" variant="outline-primary"
              class="mobile-touch-button">
              <i class="bi bi-graph-up"></i>
              <span class="d-none d-sm-inline">Line</span>
            </b-button>
            <b-button :pressed="chartViewType === 'bar'" @click="setChartViewType('bar')" variant="outline-primary"
              class="mobile-touch-button">
              <i class="bi bi-bar-chart"></i>
              <span class="d-none d-sm-inline">Bar</span>
            </b-button>
          </b-button-group>
        </div>

        <!-- Chart Type Selection -->
        <div class="col-lg-4 col-md-6 mb-3">
          <label class="form-label">Performance Metric</label>
          <b-form-select v-model="selectedChartType" :options="chartTypeOptions" @change="updateChartData"
            class="mobile-select" />
        </div>

        <!-- Time Range Selection -->
        <div class="col-lg-4 col-12 mb-3">
          <label class="form-label">Time Range</label>
          <b-button-group class="w-100 mobile-button-group time-range-group">
            <b-button v-for="range in timeRanges" :key="range.value" :pressed="timeRange === range.value"
              @click="setTimeRange(range.value)" variant="outline-primary"
              class="mobile-touch-button time-range-button">
              {{ range.text }}
            </b-button>
          </b-button-group>
        </div>
      </div>
    </div>

    <!-- Performance Insights -->
    <div v-if="insights.length > 0" class="insights-section mb-4">
      <h5>Performance Insights</h5>
      <div class="insight-cards mobile-optimized">
        <div v-for="insight in insights" :key="insight.id" class="insight-card mobile-insight-card"
          :class="insight.type">
          <div class="insight-icon">
            <i :class="getInsightIcon(insight.type)" />
          </div>
          <div class="insight-content">
            <h6>{{ insight.title }}</h6>
            <p>{{ insight.message }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart Area -->
    <div class="chart-section">
      <div v-if="!hasData" class="no-data-state">
        <i class="bi bi-graph-up text-muted" style="font-size: 3rem" />
        <h5 class="text-muted mt-3">No Performance Data Available</h5>
        <p class="text-muted">
          Start recording physical performance data to see trends and insights.
        </p>
      </div>
      <enhanced-line-chart v-else-if="chartViewType === 'line'" :chart-data="chartData" :chart-options="chartOptions"
        @point-click="onPointClick" @chart-hover="onChartHover" />

      <enhanced-bar-chart v-else-if="chartViewType === 'bar'" :chart-data="barChartData"
        :chart-options="barChartOptions" @point-click="onBarPointClick" @chart-hover="onChartHover" />
    </div>

    <!-- Performance Goals -->
    <div v-if="goals.length > 0" class="goals-section mt-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5>Performance Goals</h5>
        <b-button @click="showAddGoalModal = true" variant="outline-primary" size="sm">
          Add Goal
        </b-button>
      </div>
      <div class="goals-grid mobile-goals-grid">
        <div v-for="goal in goals" :key="goal.id" class="goal-card mobile-goal-card">
          <div class="goal-header">
            <span class="goal-metric">{{ goal.metric }}</span>
            <span class="goal-target">
              Target: {{ goal.target }}{{ getUnit(goal.metric) }}
            </span>
          </div>
          <b-progress :value="goal.progress" :max="100" :variant="getProgressVariant(goal.progress)" height="8px"
            class="mb-2" />
          <div class="goal-footer">
            <span class="goal-progress">{{ goal.progress }}% Complete</span>
            <b-button size="sm" variant="outline-danger" @click="deleteGoal(goal.id)">
              Delete
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Export Options -->
    <div class="export-section mt-4">
      <b-button-group>
        <b-button variant="outline-secondary" @click="exportData('csv')">
          Export CSV
        </b-button>
        <b-button variant="outline-secondary" @click="exportData('pdf')">
          Export PDF
        </b-button>
      </b-button-group>
    </div>

    <!-- Add Goal Modal -->
    <b-modal v-model="showAddGoalModal" title="Add Performance Goal" @ok="addGoal">
      <b-form-group label="Metric">
        <b-form-select v-model="newGoal.metric" :options="goalMetricOptions" />
      </b-form-group>
      <b-form-group label="Target Value">
        <b-form-input v-model="newGoal.target" type="number" step="0.01" />
      </b-form-group>
      <b-form-group label="Target Date">
        <VueDatePicker :locale="'en-GB'" v-model="newGoal.deadline" name="targetDate" id="targetDate"
          :enable-time-picker="false" auto-apply format="dd/MM/yyyy" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import EnhancedLineChart from "@/components/charts/EnhancedLineChart.vue";
import EnhancedBarChart from "@/components/charts/EnhancedBarChart.vue";
import {
  METRICS,
  isValidValue,
  fieldForChartType,
} from "@/utils/physicalPerformanceMetrics";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  name: "PhysicalPerformanceTrends",
  components: {
    EnhancedLineChart,
    EnhancedBarChart,
    VueDatePicker,
  },
  props: {
    performanceData: {
      type: Array,
      default: () => [],
    },
    // Welfare guardrail: weight trends are staff-only. When false, the Player
    // Weight Chart option is hidden. See useNutritionAccess.js.
    canSeeWeight: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      selectedChartType: "1",
      chartViewType: "bar", // Default to bar chart
      timeRange: "1year",
      showAddGoalModal: false,
      chartKey: 0,

      timeRanges: [
        { text: "1M", value: "1month" },
        { text: "3M", value: "3months" },
        { text: "6M", value: "6months" },
        { text: "1Y", value: "1year" },
      ],

      chartTypeOptionsBase: [
        { value: "1", text: "10m SPRINT" },
        { value: "7", text: "30m SPRINT" },
        { value: "2", text: "Countermovement Jump Chart" },
        { value: "3", text: "Reactive Strength Index Chart" },
        { value: "4", text: "Fitness Test Time" },
        { value: "5", text: "Player Height Chart" },
        { value: "6", text: "Player Weight Chart" },
      ],

      newGoal: {
        metric: null,
        target: null,
        deadline: null,
      },

      goals: [],
      insights: [],
    };
  },

  computed: {
    // Hide the Player Weight Chart (value "6") from non-staff viewers.
    chartTypeOptions() {
      if (this.canSeeWeight) return this.chartTypeOptionsBase;
      return this.chartTypeOptionsBase.filter((o) => o.value !== "6");
    },
    hasData() {
      return (
        Array.isArray(this.performanceData) && this.performanceData.length > 0
      );
    },

    filteredData() {
      if (!this.hasData) return [];

      const data = this.performanceData;
      const now = new Date();
      let startDate = new Date();

      switch (this.timeRange) {
        case "1month":
          startDate.setMonth(now.getMonth() - 1);
          break;
        case "3months":
          startDate.setMonth(now.getMonth() - 3);
          break;
        case "6months":
          startDate.setMonth(now.getMonth() - 6);
          break;
        case "1year":
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate.setMonth(now.getMonth() - 3);
      }

      return data.filter((item) => new Date(item.date) >= startDate);
    },

    chartData() {
      if (!this.hasData) return { labels: [], datasets: [] };

      // Enhanced filtering to exclude invalid/empty data from chart display
      // isValidValue is shared from @/utils/physicalPerformanceMetrics

      // Get valid data points with corresponding labels
      const validDataPoints = [];
      const validLabels = [];

      this.filteredData.forEach((item) => {
        let value;
        switch (this.selectedChartType) {
          case "1":
            value = item.tenM_sprint_time;
            break;
          case "2":
            value = item.cuntermovement_jump;
            break;
          case "3":
            value = item.reactive_strength_index_average;
            break;
          case "4":
            value = item.thirty_15_intermittent_fitness_test;
            break;
          case "5":
            value = item.height;
            break;
          case "6":
            value = item.weight;
            break;
          case "7":
            value = item.fortyM_sprint_time;
            break;
          default:
            value = null;
        }

        if (isValidValue(value)) {
          validDataPoints.push(value);
          validLabels.push(new Date(item.date).toLocaleDateString("en-GB"));
        }
      });

      let dataset = {};

      switch (this.selectedChartType) {
        case "1":
          dataset = {
            label: "10m Sprint Time",
            data: validDataPoints,
            borderColor: "#007bff",
            backgroundColor: "rgba(0, 123, 255, 0.1)",
          };
          break;
        case "2":
          dataset = {
            label: "Countermovement Jump",
            data: validDataPoints,
            borderColor: "#28a745",
            backgroundColor: "rgba(40, 167, 69, 0.1)",
          };
          break;
        case "3":
          dataset = {
            label: "Reactive Strength Index",
            data: validDataPoints,
            borderColor: "#ffc107",
            backgroundColor: "rgba(255, 193, 7, 0.1)",
          };
          break;
        case "4":
          dataset = {
            label: "Fitness Test",
            data: validDataPoints,
            borderColor: "#dc3545",
            backgroundColor: "rgba(220, 53, 69, 0.1)",
          };
          break;
        case "5":
          dataset = {
            label: "Height",
            data: validDataPoints,
            borderColor: "#6f42c1",
            backgroundColor: "rgba(111, 66, 193, 0.1)",
          };
          break;
        case "6":
          dataset = {
            label: "Weight",
            data: validDataPoints,
            borderColor: "#fd7e14",
            backgroundColor: "rgba(253, 126, 20, 0.1)",
          };
          break;
        case "7":
          dataset = {
            label: "30m Sprint Time",
            data: validDataPoints,
            borderColor: "#17a2b8",
            backgroundColor: "rgba(23, 162, 184, 0.1)",
          };
          break;
        default:
          dataset = {
            label: "No Data",
            data: [],
            borderColor: "#6c757d",
            backgroundColor: "rgba(108, 117, 125, 0.1)",
          };
      }

      return {
        labels: validLabels,
        datasets: [dataset],
      };
    },

    chartOptions() {
      const isMobile = window.innerWidth <= 768;

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: isMobile ? 600 : 1000,
          easing: "easeInOutQuart",
        },
        plugins: {
          legend: {
            position: isMobile ? "bottom" : "top",
            labels: {
              padding: isMobile ? 10 : 20,
              font: {
                size: isMobile ? 11 : 12,
              },
              usePointStyle: true,
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            bodyFont: {
              size: isMobile ? 11 : 12,
            },
            titleFont: {
              size: isMobile ? 12 : 14,
            },
            padding: isMobile ? 8 : 12,
            cornerRadius: 6,
            displayColors: true,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
          },
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: !isMobile,
              text: this.getYAxisLabel(),
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              font: {
                size: isMobile ? 10 : 11,
              },
              maxTicksLimit: isMobile ? 5 : 8,
            },
            grid: {
              display: true,
              color: "rgba(0,0,0,0.1)",
            },
          },
          x: {
            title: {
              display: !isMobile,
              text: "Date",
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              maxRotation: isMobile ? 45 : 0,
              maxTicksLimit: isMobile ? 4 : 8,
            },
            grid: {
              display: false,
            },
          },
        },
        elements: {
          point: {
            radius: isMobile ? 3 : 4,
            hoverRadius: isMobile ? 5 : 6,
            borderWidth: isMobile ? 1 : 2,
          },
          line: {
            tension: 0.4,
            borderWidth: isMobile ? 2 : 3,
          },
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
      };
    },

    goalMetricOptions() {
      return this.chartTypeOptions.map((option) => ({
        value: option.text,
        text: option.text,
      }));
    },

    barChartData() {
      if (!this.hasData) return { labels: [], datasets: [] };

      // Enhanced filtering to exclude invalid/empty data from bar chart display
      // isValidValue is shared from @/utils/physicalPerformanceMetrics

      // Get valid data points with corresponding labels
      const validDataPoints = [];
      const validLabels = [];

      this.filteredData.forEach((item) => {
        let value;
        switch (this.selectedChartType) {
          case "1":
            value = item.tenM_sprint_time;
            break;
          case "2":
            value = item.cuntermovement_jump;
            break;
          case "3":
            value = item.reactive_strength_index_average;
            break;
          case "4":
            value = item.thirty_15_intermittent_fitness_test;
            break;
          case "5":
            value = item.height;
            break;
          case "6":
            value = item.weight;
            break;
          case "7":
            value = item.fortyM_sprint_time;
            break;
          default:
            value = null;
        }

        if (isValidValue(value)) {
          validDataPoints.push(value);
          validLabels.push(new Date(item.date).toLocaleDateString("en-GB"));
        }
      });

      let dataset = {};

      switch (this.selectedChartType) {
        case "1":
          dataset = {
            label: "10m Sprint Time",
            data: validDataPoints,
            backgroundColor: "rgba(0, 123, 255, 0.6)",
            borderColor: "#007bff",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "2":
          dataset = {
            label: "Countermovement Jump",
            data: validDataPoints,
            backgroundColor: "rgba(40, 167, 69, 0.6)",
            borderColor: "#28a745",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "3":
          dataset = {
            label: "Reactive Strength Index",
            data: validDataPoints,
            backgroundColor: "rgba(255, 193, 7, 0.6)",
            borderColor: "#ffc107",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "4":
          dataset = {
            label: "Fitness Test",
            data: validDataPoints,
            backgroundColor: "rgba(220, 53, 69, 0.6)",
            borderColor: "#dc3545",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "5":
          dataset = {
            label: "Height",
            data: validDataPoints,
            backgroundColor: "rgba(111, 66, 193, 0.6)",
            borderColor: "#6f42c1",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "6":
          dataset = {
            label: "Weight",
            data: validDataPoints,
            backgroundColor: "rgba(253, 126, 20, 0.6)",
            borderColor: "#fd7e14",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        case "7":
          dataset = {
            label: "30m Sprint Time",
            data: validDataPoints,
            backgroundColor: "rgba(23, 162, 184, 0.6)",
            borderColor: "#17a2b8",
            borderWidth: 2,
            borderRadius: 4,
          };
          break;
        default:
          dataset = {
            label: "No Data",
            data: [],
            backgroundColor: "rgba(108, 117, 125, 0.6)",
            borderColor: "#6c757d",
            borderWidth: 2,
            borderRadius: 4,
          };
      }

      return {
        labels: validLabels,
        datasets: [dataset],
      };
    },

    barChartOptions() {
      const isMobile = window.innerWidth <= 768;

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: isMobile ? 800 : 1200,
          easing: "easeInOutQuart",
          delay: (context) => context.dataIndex * (isMobile ? 50 : 100),
        },
        plugins: {
          legend: {
            position: isMobile ? "bottom" : "top",
            labels: {
              padding: isMobile ? 10 : 20,
              font: {
                size: isMobile ? 11 : 12,
              },
              usePointStyle: true,
            },
          },
          tooltip: {
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            bodyFont: {
              size: isMobile ? 11 : 12,
            },
            titleFont: {
              size: isMobile ? 12 : 14,
            },
            padding: isMobile ? 8 : 12,
            cornerRadius: 6,
            callbacks: {
              title: (context) => `Date: ${context[0].label}`,
              label: (context) => {
                const unit = this.getUnit(context.dataset.label);
                return `${context.dataset.label}: ${context.parsed.y}${unit}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: false,
            title: {
              display: !isMobile,
              text: this.getYAxisLabel(),
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              font: {
                size: isMobile ? 10 : 11,
              },
              maxTicksLimit: isMobile ? 5 : 8,
            },
            grid: {
              display: true,
              color: "rgba(0,0,0,0.1)",
            },
          },
          x: {
            title: {
              display: !isMobile,
              text: "Date",
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              font: {
                size: isMobile ? 9 : 11,
              },
              maxRotation: isMobile ? 45 : 0,
              maxTicksLimit: isMobile ? 4 : 8,
            },
            grid: {
              display: false,
            },
          },
        },
        elements: {
          bar: {
            borderWidth: isMobile ? 1 : 2,
            borderRadius: isMobile ? 3 : 4,
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
      };
    },
  },

  watch: {
    performanceData: {
      handler() {
        this.generateInsights();
      },
      deep: true,
    },

    selectedChartType() {
      // Chart will automatically animate when data changes
      this.generateInsights();
    },
  },

  mounted() {
    this.generateInsights();
  },

  methods: {
    setTimeRange(range) {
      this.timeRange = range;
      this.generateInsights();
    },

    updateChartData() {
      // Update chart data with animation
      this.generateInsights();
    },

    setChartViewType(type) {
      this.chartViewType = type;
      this.updateChartData();
    },

    onBarPointClick(pointIndex, datasetIndex) {
      // Handle bar click for detailed view
      console.log("Bar clicked:", pointIndex, datasetIndex);
    },

    generateInsights() {
      console.log("Generating insights...");
      this.insights = [];

      if (!this.hasData || this.filteredData.length < 2) return;

      const currentMetric = this.getCurrentMetricData();
      if (!currentMetric || currentMetric.length < 3) return;

      // Use recent trend analysis instead of comparing oldest to newest
      // Take the last 30% of data points as "recent" and compare against the previous 30%
      const dataLength = currentMetric.length;
      const recentSize = Math.max(2, Math.floor(dataLength * 0.3));
      const previousSize = Math.max(2, Math.floor(dataLength * 0.3));

      // Ensure we have enough data for meaningful comparison
      if (dataLength < recentSize + previousSize) return;

      // Get recent values (latest data points)
      const recentValues = currentMetric.slice(-recentSize);
      // Get previous values (data points before the recent ones, but not the very oldest)
      const previousValues = currentMetric.slice(
        -(recentSize + previousSize),
        -recentSize,
      );

      // Calculate averages for more stable comparison
      const recentAvg =
        recentValues.reduce((sum, val) => sum + val, 0) / recentValues.length;
      const previousAvg =
        previousValues.reduce((sum, val) => sum + val, 0) /
        previousValues.length;

      // Prevent division by zero and handle edge cases
      if (
        previousAvg === 0 ||
        recentAvg === 0 ||
        !isFinite(previousAvg) ||
        !isFinite(recentAvg)
      ) {
        return;
      }

      const trend = ((recentAvg - previousAvg) / previousAvg) * 100;

      // Additional check for non-finite results (Infinity, -Infinity, NaN)
      if (!isFinite(trend)) {
        return;
      }

      if (Math.abs(trend) > 5) {
        // Determine if this metric type is better when lower (sprint times) or higher (jumps, etc.)
        const isLowerBetter = this.isMetricLowerBetter(this.selectedChartType);

        let direction, color;
        if (isLowerBetter) {
          // For sprint times: negative trend (decrease in time) = improvement
          direction = trend < 0 ? "improved" : "declined";
          color = trend < 0 ? "success" : "warning";
        } else {
          // For jumps, strength: positive trend (increase in value) = improvement
          direction = trend > 0 ? "improved" : "declined";
          color = trend > 0 ? "success" : "warning";
        }

        this.insights.push({
          id: "trend",
          type: color,
          title: `Recent Performance ${direction}`,
          message: `Your recent performance has ${direction} by ${Math.abs(trend).toFixed(1)}% compared to previous sessions.`,
        });
      }

      // Add consistency insight
      const consistency = this.calculateConsistency(currentMetric);
      if (consistency < 0.8) {
        this.insights.push({
          id: "consistency",
          type: "info",
          title: "Consistency Opportunity",
          message:
            "Your performance varies significantly. Focus on consistent training routines.",
        });
      }
    },

    getCurrentMetricData() {
      // Enhanced filtering to exclude invalid/empty data (same as main component)
      // isValidValue is shared from @/utils/physicalPerformanceMetrics

      switch (this.selectedChartType) {
        case "1":
          return this.filteredData
            .map((item) => item.tenM_sprint_time)
            .filter(isValidValue);
        case "2":
          return this.filteredData
            .map((item) => item.cuntermovement_jump)
            .filter(isValidValue);
        case "3":
          return this.filteredData
            .map((item) => item.reactive_strength_index_average)
            .filter(isValidValue);
        case "4":
          return this.filteredData
            .map((item) => item.thirty_15_intermittent_fitness_test)
            .filter(isValidValue);
        case "5":
          return this.filteredData
            .map((item) => item.height)
            .filter(isValidValue);
        case "6":
          return this.filteredData
            .map((item) => item.weight)
            .filter(isValidValue);
        case "7":
          return this.filteredData
            .map((item) => item.fortyM_sprint_time)
            .filter(isValidValue);
        default:
          return [];
      }
    },

    calculateConsistency(values) {
      if (values.length < 2) return 1;

      const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
      const variance =
        values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
        values.length;
      const stdDev = Math.sqrt(variance);

      return 1 - stdDev / mean;
    },

    getYAxisLabel() {
      const field = fieldForChartType(this.selectedChartType);
      return field ? METRICS[field].axis : "Value";
    },

    getUnit(metric) {
      const units = {
        "10m SPRINT": "s",
        "30m SPRINT": "s",
        "Countermovement Jump Chart": "cm",
        "Reactive Strength Index Chart": "",
        "Fitness Test Time": "",
        "Player Height Chart": "cm",
        "Player Weight Chart": "kg",
      };

      return units[metric] || "";
    },

    getInsightIcon(type) {
      const icons = {
        success: "bi bi-trending-up text-success",
        warning: "bi bi-trending-down text-warning",
        info: "bi bi-info-circle text-info",
      };

      return icons[type] || "bi bi-info-circle";
    },

    getProgressVariant(progress) {
      if (progress >= 80) return "success";
      if (progress >= 60) return "warning";
      return "danger";
    },

    onPointClick(pointIndex, datasetIndex) {
      // Handle point click for detailed view
      console.log("Point clicked:", pointIndex, datasetIndex);
    },

    onChartHover(event, elements) {
      // Handle chart hover
      console.log("Chart hover:", event, elements);
    },

    addGoal() {
      if (!this.newGoal.metric || !this.newGoal.target) return;

      const goal = {
        id: Date.now(),
        metric: this.newGoal.metric,
        target: parseFloat(this.newGoal.target),
        deadline: this.newGoal.deadline,
        progress: Math.random() * 100, // This should be calculated based on actual data
      };

      this.goals.push(goal);

      this.newGoal = {
        metric: null,
        target: null,
        deadline: null,
      };
    },

    deleteGoal(goalId) {
      this.goals = this.goals.filter((goal) => goal.id !== goalId);
    },

    exportData(format) {
      if (format === "csv") {
        this.exportToCSV();
      } else if (format === "pdf") {
        this.exportToPDF();
      }
    },

    exportToCSV() {
      const data = this.filteredData;
      const csvContent = this.convertToCSV(data);
      this.downloadFile(csvContent, "performance-data.csv", "text/csv");
    },

    exportToPDF() {
      // This would require a PDF library like jsPDF
      console.log("PDF export not yet implemented");
    },

    convertToCSV(data) {
      if (!data.length) return "";

      const headers = Object.keys(data[0]).join(",");
      const rows = data.map((row) => Object.values(row).join(",")).join("\n");

      return `${headers}\n${rows}`;
    },

    downloadFile(content, filename, contentType) {
      const blob = new Blob([content], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    },

    isMetricLowerBetter(chartType) {
      const field = fieldForChartType(chartType);
      return field ? METRICS[field].lowerIsBetter : false;
    },
  },
};
</script>

<style scoped>
.physical-performance-trends-enhanced {
  padding: 1rem;
}

.controls-section {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.insights-section {
  margin-bottom: 1.5rem;
}

.insight-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.insight-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  background: white;
}

.insight-card.success {
  border-left: 4px solid #28a745;
}

.insight-card.warning {
  border-left: 4px solid #ffc107;
}

.insight-card.info {
  border-left: 4px solid #17a2b8;
}

.insight-icon {
  font-size: 1.5rem;
  margin-right: 1rem;
}

.insight-content h6 {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
}

.insight-content p {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.chart-section {
  min-height: 400px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  background: white;
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
}

.goals-section {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.goal-card {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.goal-header {
  display: flex;
  justify-content: between;
  margin-bottom: 0.5rem;
}

.goal-metric {
  font-weight: 600;
}

.goal-target {
  font-size: 0.9rem;
  color: #6c757d;
}

.goal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
}

.goal-progress {
  font-size: 0.9rem;
  color: #6c757d;
}

.export-section {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .physical-performance-trends-enhanced {
    padding: 0.5rem;
  }

  .controls-section {
    padding: 0.75rem;
  }

  /* Mobile-friendly button groups */
  .mobile-button-group .btn {
    min-height: 48px;
    font-size: 14px;
    padding: 12px 8px;
  }

  .mobile-touch-button {
    min-width: 44px;
    touch-action: manipulation;
  }

  .time-range-group .time-range-button {
    min-width: 60px;
    font-size: 13px;
  }

  /* Mobile select styling */
  .mobile-select {
    min-height: 48px;
    font-size: 14px;
  }

  /* Chart section mobile optimization */
  .chart-section {
    min-height: 320px;
    padding: 0.75rem;
  }

  /* Mobile-optimized insight cards */
  .insight-cards.mobile-optimized {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .mobile-insight-card {
    padding: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .mobile-insight-card .insight-icon {
    margin-right: 0;
    margin-bottom: 0.5rem;
    align-self: flex-start;
  }

  .mobile-insight-card .insight-content h6 {
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }

  .mobile-insight-card .insight-content p {
    font-size: 0.85rem;
    line-height: 1.4;
  }

  /* Mobile goals grid */
  .mobile-goals-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .mobile-goal-card {
    padding: 0.75rem;
  }

  .mobile-goal-card .goal-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .mobile-goal-card .goal-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  /* Export section mobile */
  .export-section {
    padding: 0.75rem;
  }

  .export-section .btn-group {
    width: 100%;
  }

  .export-section .btn {
    min-height: 48px;
    flex: 1;
  }

  /* Form labels on mobile */
  .form-label {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  /* No data state mobile */
  .no-data-state {
    height: 250px;
    padding: 1rem;
  }

  .no-data-state h5 {
    font-size: 1.1rem;
    text-align: center;
  }

  .no-data-state p {
    font-size: 0.9rem;
    text-align: center;
  }
}

/* Extra small devices optimization */
@media (max-width: 576px) {
  .physical-performance-trends-enhanced {
    padding: 0.25rem;
  }

  .controls-section {
    padding: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .chart-section {
    min-height: 280px;
    padding: 0.5rem;
  }

  .mobile-button-group .btn {
    font-size: 12px;
    padding: 10px 6px;
  }

  .time-range-group .time-range-button {
    min-width: 50px;
    font-size: 12px;
  }

  .mobile-insight-card .insight-content h6 {
    font-size: 0.95rem;
  }

  .mobile-insight-card .insight-content p {
    font-size: 0.8rem;
  }

  .insights-section h5,
  .goals-section h5 {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  /* Stack export buttons vertically on very small screens */
  .export-section .btn-group {
    flex-direction: column;
  }

  .export-section .btn {
    margin-bottom: 0.5rem;
  }

  .export-section .btn:last-child {
    margin-bottom: 0;
  }
}

/* Landscape mobile optimization */
@media (max-width: 768px) and (orientation: landscape) {
  .chart-section {
    min-height: 250px;
  }

  .no-data-state {
    height: 200px;
  }

  .mobile-insight-card {
    flex-direction: row;
    align-items: center;
  }

  .mobile-insight-card .insight-icon {
    margin-right: 0.75rem;
    margin-bottom: 0;
  }
}

/* Touch-friendly improvements */
@media (hover: none) and (pointer: coarse) {
  .mobile-touch-button {
    transform: scale(1);
    transition: transform 0.1s ease;
  }

  .mobile-touch-button:active {
    transform: scale(0.98);
  }

  .goal-card,
  .insight-card {
    transition: box-shadow 0.2s ease;
  }

  .goal-card:active,
  .insight-card:active {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}
</style>
